/**
 * WidgetSlot — labeled placeholder card per the dashboard surface
 * (Sketched in Claude Design 2026-05-10).
 *
 * Structure: title row with a 32×32 icon chip + (title 14/600) + optional
 * description (12/fg-tertiary). Body is a dashed-border `bg-sunken`
 * callout containing "Awaiting data integration".
 *
 * SCRUM-58 visual-fidelity round (2026-05-10):
 *  - Added the 32×32 `bg-sunken radius-lg` icon chip next to the title
 *    (was missing — slot rendered as plain text).
 *  - Body text now sits inside a dashed-border `radius-xl` callout
 *    instead of plain prose, matching the "this slot is waiting for
 *    data" visual contract from the handoff.
 *  - Added `icon: IconName` prop so each slot can specify its own glyph.
 *
 * SCRUM-99 kit consolidation (2026-05-15):
 *  - Outer surface shell composes the kit Card primitive
 *    (`variant="surface"`, `padding="md"`) via `asChild` so the
 *    semantic `<article>` tag + `aria-labelledby` + prop-driven
 *    `minHeight` inline style stay at this call site. Card supplies
 *    the bg-surface + border-subtle + rounded-2xl + p-5 shape.
 *  - Inner "Awaiting data integration" callout composes the kit Card
 *    primitive (`variant="sunken"`, `padding="sm"`). Card supplies the
 *    bg-sunken + dashed border + rounded-xl + p-3 shape; this widget
 *    keeps the flex centering + typography classes.
 */
import * as React from 'react';
import { Card, Icon, type IconName } from '@sparta/ui-kit';

export interface WidgetSlotProps {
  title: string;
  description?: string;
  /** Glyph rendered in the 32×32 chip next to the title. */
  icon?: IconName;
  /** Min height in pixels. Default 180. */
  minH?: number;
}

const PLACEHOLDER_BODY = 'Awaiting data integration';

function slotIdFromTitle(title: string): string {
  return `widget-${title.replace(/\s+/g, '-').toLowerCase()}`;
}

export function WidgetSlot({
  title,
  description,
  icon = 'grid',
  minH = 180,
}: WidgetSlotProps): React.ReactElement {
  const headingId = slotIdFromTitle(title);
  return (
    <Card asChild variant="surface" padding="md">
      <article
        aria-labelledby={headingId}
        className="flex flex-col gap-3"
        // ADR frontend-0002 exemption 1: minH is a prop-driven JS value.
        style={{ minHeight: minH }}
      >
        <div className="flex items-center gap-2.5">
          <div
            aria-hidden="true"
            className="w-8 h-8 rounded-lg bg-[var(--bg-sunken)] text-[var(--fg-secondary)] inline-flex items-center justify-center shrink-0"
          >
            <Icon name={icon} size={16} />
          </div>
          <div className="min-w-0">
            {/* h2 keeps the page outline h1 (greeting) → h2 (widget titles)
                so axe's heading-order rule doesn't trip. */}
            <h2
              id={headingId}
              className="text-sm font-semibold text-[var(--fg-primary)] m-0 leading-[1.3]"
            >
              {title}
            </h2>
            {description !== undefined ? (
              <p
                className="text-xs text-[var(--fg-tertiary)]"
                // ADR frontend-0002 exemption 3: CSS margin shorthand reset.
                // Tailwind's `mt-0.5` does not zero the default browser left/right
                // margins on a <p>; the four-sided shorthand is the cleanest fix.
                style={{ margin: '2px 0 0' }}
              >
                {description}
              </p>
            ) : null}
          </div>
        </div>
        <Card
          variant="sunken"
          padding="sm"
          className="flex-1 flex items-center justify-center text-[var(--fg-tertiary)] text-xs font-medium text-center"
        >
          {PLACEHOLDER_BODY}
        </Card>
      </article>
    </Card>
  );
}
